/**
 * Module je Instanz: welche Services ein Betrieb gebucht hat. Das Admin Center schaltet sie
 * pro Instanz an und ab (Tabelle instance_modules; fehlende Zeile = defaultEnabled).
 *
 * Wirkung: Der Server streicht beim Auflösen der Rechte alle Capabilities abgewählter Module
 * (auch für Inhaber und Admins), die Modul-Middleware sperrt die API-Prefixe, und der Client
 * blendet Navigation, Settings-Seiten und Quer-Links aus. Übersicht, Benachrichtigungen und
 * Einstellungen bleiben immer an.
 *
 * Wird von Client und Server importiert. Das Admin Center hält eine Kopie des Katalogs
 * (server/services/moduleCatalog.ts dort) – bei Änderungen an Keys, Labels oder Defaults
 * beide Seiten pflegen.
 */

export const INSTANCE_MODULE_KEYS = [
  'kichat',
  'posteingang',
  'plantafel',
  'aufgaben',
  'projekte',
  'angebote',
  'auftraege',
  'rechnungen',
  'kontakte',
  'stammdaten',
  'ausschreibungen',
  'vorlagen',
  'hr',
  'fuhrpark',
  'field_app',
  'datev',
  'dkv',
  'meinshk',
  'omd',
  'ids',
  'metal',
  'lead_api',
] as const;

export type InstanceModuleKey = (typeof INSTANCE_MODULE_KEYS)[number];
export type InstanceModuleMap = Record<InstanceModuleKey, boolean>;
export type InstanceModuleGroup = 'module' | 'integration' | 'app';
export type IntegrationModuleId = 'datev' | 'omd' | 'ids' | 'metal' | 'dkv' | 'meinshk' | 'lead_api';

export type InstanceModule = {
  key: InstanceModuleKey;
  label: string;
  description: string;
  group: InstanceModuleGroup;
  /** Gilt ohne Zeile in instance_modules. */
  defaultEnabled: boolean;
  /**
   * Module, ohne die dieses Modul nicht funktioniert (z. B. Projekte braucht Kontakte für die
   * Kundenauswahl). Ist eines davon aus, gilt dieses Modul auch als aus; das Admin Center schaltet
   * die Abhängigkeiten beim Speichern mit.
   */
  requires?: readonly InstanceModuleKey[];
  /**
   * Rechte des Moduls: Prefixe (z. B. "web.hr." fängt auch künftige Keys) und exakte Keys.
   * Ein Recht darf mehreren Modulen gehören; dann müssen alle an sein (web.rechnungen.datev).
   */
  capabilityPrefixes?: readonly string[];
  capabilities?: readonly string[];
  /** API-Pfade relativ zu /api; der längste passende Prefix gewinnt. */
  apiPrefixes: readonly string[];
  /** Client-Pfade nach /i/:instanceId. */
  routePrefixes: readonly string[];
  /** Recht, das zusätzlich zum Modul nötig ist, um die Seite zu öffnen. */
  viewCapability?: string;
  /** Settings-Unterseiten, die mit dem Modul verschwinden. */
  settingsPaths?: readonly string[];
  integrationId?: IntegrationModuleId;
};

export const INSTANCE_MODULES: readonly InstanceModule[] = [
  {
    key: 'kichat',
    label: 'KI Chat',
    description: 'KI-Assistent im Backoffice mit Skills und geplanten Läufen.',
    group: 'module',
    defaultEnabled: true,
    capabilities: ['web.kichat.view'],
    apiPrefixes: ['/ai'],
    routePrefixes: ['/chat'],
    viewCapability: 'web.kichat.view',
    settingsPaths: ['/settings/assistant'],
  },
  {
    key: 'posteingang',
    label: 'Posteingang',
    description: 'Gemeinsames E-Mail-Postfach mit Projektzuordnung.',
    group: 'module',
    defaultEnabled: true,
    capabilityPrefixes: ['web.posteingang.'],
    apiPrefixes: ['/inbox', '/settings/inbox-reply'],
    routePrefixes: ['/inbox'],
    viewCapability: 'web.posteingang.view',
  },
  {
    key: 'plantafel',
    label: 'Plantafel',
    description: 'Termine, Einsatzplanung, Liste und Karte.',
    group: 'module',
    defaultEnabled: true,
    capabilityPrefixes: ['web.plantafel.'],
    apiPrefixes: ['/calendar', '/plantafel', '/settings/plantafel'],
    routePrefixes: ['/calendar'],
    viewCapability: 'web.plantafel.view',
    settingsPaths: ['/settings/plantafel'],
  },
  {
    key: 'aufgaben',
    label: 'To-dos',
    description: 'To-dos im Team mit Ansichten und Checklisten.',
    group: 'module',
    defaultEnabled: true,
    capabilityPrefixes: ['web.aufgaben.'],
    apiPrefixes: ['/tasks'],
    routePrefixes: ['/todos', '/aufgaben'],
    viewCapability: 'web.aufgaben.own',
  },
  {
    key: 'projekte',
    label: 'Projekte',
    description: 'Anfragen, Angebote, Aufträge und Projektdokumente.',
    group: 'module',
    defaultEnabled: true,
    // Projekt-Kommentare in der App gehören fachlich zu Projekte.
    capabilityPrefixes: ['web.projekte.', 'app.kommentare.'],
    requires: ['kontakte'],
    apiPrefixes: [
      '/leads',
      '/projects',
      '/offerings',
      '/offering-positions',
      '/lead-documents',
      '/lead-positions',
      '/settings/lead-pipelines',
    ],
    routePrefixes: ['/projects', '/leads'],
    viewCapability: 'web.projekte.view',
    settingsPaths: ['/settings/lead-pipeline', '/settings/lead-categories'],
  },
  {
    key: 'angebote',
    label: 'Angebote',
    description: 'Angebotseditor und Kalkulation, Angebot und Kostenvoranschlag, LV-Importe.',
    group: 'module',
    defaultEnabled: true,
    // Web: Angebote nutzen web.projekte.*; gesperrt wird über Belegtyp und Routen. App: KI-Angebote im Chat.
    capabilities: ['app.kichat.angebote'],
    requires: ['projekte'],
    apiPrefixes: ['/gaeb', '/ugl', '/lv-pdf'],
    routePrefixes: [],
  },
  {
    key: 'auftraege',
    label: 'Auftragsabwicklung',
    description: 'Auftragsbestätigung, Lieferschein und Arbeitsbericht.',
    group: 'module',
    defaultEnabled: true,
    requires: ['projekte'],
    apiPrefixes: [],
    routePrefixes: [],
  },
  {
    key: 'rechnungen',
    label: 'Rechnungen',
    description: 'Ausgangs- und Eingangsrechnungen, Geschäftsdokumente.',
    group: 'module',
    defaultEnabled: true,
    capabilityPrefixes: ['web.rechnungen.'],
    // Rechnungen aus der Mobile App gehören fachlich zu Rechnungen.
    capabilities: ['app.rechnungen.create'],
    requires: ['kontakte'],
    apiPrefixes: ['/invoices', '/business-documents', '/business-document-folders', '/settings/invoice-forwarding'],
    routePrefixes: ['/invoices', '/rechnungen'],
    viewCapability: 'web.rechnungen.view',
    settingsPaths: ['/settings/invoice-forwarding'],
  },
  {
    key: 'kontakte',
    label: 'Kontakte',
    description: 'Kunden, Organisationen und Ansprechpartner.',
    group: 'module',
    defaultEnabled: true,
    capabilityPrefixes: ['web.kontakte.'],
    capabilities: ['app.kontakte.view'],
    apiPrefixes: ['/contacts', '/organizations'],
    routePrefixes: ['/contacts', '/organizations'],
    viewCapability: 'web.kontakte.view',
  },
  {
    key: 'stammdaten',
    label: 'Stammdaten',
    description: 'Materialien, Leistungen und Lieferantenkataloge.',
    group: 'module',
    defaultEnabled: true,
    capabilityPrefixes: ['web.stammdaten.'],
    apiPrefixes: [
      '/materials',
      '/material-folders',
      '/leistungen',
      '/leistung-folders',
      '/suppliers',
      '/instance-material-settings',
      '/instance-discount-groups',
      '/instance-settings',
      '/datanorm',
      '/embedding',
      '/settings/suppliers',
    ],
    routePrefixes: ['/stammdaten'],
    viewCapability: 'web.stammdaten.view',
  },
  {
    key: 'ausschreibungen',
    label: 'Ausschreibungen',
    description: 'GAEB-Leistungsverzeichnisse auswerten und bearbeiten.',
    group: 'module',
    defaultEnabled: true,
    capabilityPrefixes: ['web.ausschreibungen.'],
    requires: ['kontakte'],
    apiPrefixes: ['/ausschreibungen'],
    routePrefixes: ['/ausschreibungen'],
    viewCapability: 'web.ausschreibungen.view',
  },
  {
    key: 'vorlagen',
    label: 'Vorlagen',
    description: 'Dokument- und E-Mail-Vorlagen, LV-Vorlagen, E-Signatur.',
    group: 'module',
    defaultEnabled: true,
    capabilityPrefixes: ['web.vorlagen.'],
    apiPrefixes: ['/templates', '/offering-vorlagen', '/offering-vorlage-folders'],
    routePrefixes: ['/templates'],
    viewCapability: 'web.vorlagen.view',
  },
  {
    key: 'hr',
    label: 'Mitarbeiter',
    description: 'Personalakte, Zeiterfassung, Anträge und Lohnbuchhaltung.',
    group: 'module',
    defaultEnabled: true,
    // Zeiterfassung in der App gehört fachlich zu Mitarbeiter.
    capabilityPrefixes: ['web.hr.', 'app.zeit.'],
    apiPrefixes: ['/employees', '/settings/employee-document-types', '/settings/payroll-adjustment-lohnarten'],
    routePrefixes: ['/mitarbeiter'],
    viewCapability: 'web.hr.view',
    settingsPaths: ['/settings/payroll-adjustment-lohnarten', '/settings/employee-document-types'],
  },
  {
    key: 'fuhrpark',
    label: 'Fuhrpark',
    description: 'Fahrzeuge, Wartung, Dokumente und Tankungen.',
    group: 'module',
    defaultEnabled: true,
    capabilityPrefixes: ['web.fuhrpark.'],
    apiPrefixes: ['/fuhrpark'],
    routePrefixes: ['/fleet'],
    viewCapability: 'web.fuhrpark.view',
  },
  {
    key: 'field_app',
    label: 'Mobile App',
    description: 'Meisterpfad App für Monteure: Zeiterfassung, Kalender, Kommentare.',
    group: 'app',
    defaultEnabled: true,
    capabilityPrefixes: ['app.'],
    apiPrefixes: ['/field', '/ai/field'],
    routePrefixes: [],
  },
  {
    key: 'datev',
    label: 'DATEV',
    description: 'Rechnungen und Belege als DATEV-Export für die Steuerkanzlei.',
    group: 'integration',
    defaultEnabled: true,
    capabilities: ['web.rechnungen.datev'],
    requires: ['rechnungen'],
    apiPrefixes: ['/datev', '/settings/datev'],
    routePrefixes: [],
    settingsPaths: ['/settings/integrations/datev'],
    integrationId: 'datev',
  },
  {
    key: 'dkv',
    label: 'DKV Mobility',
    description: 'Tankkarten-Transaktionen automatisch in den Fuhrpark übernehmen.',
    group: 'integration',
    defaultEnabled: true,
    requires: ['fuhrpark'],
    apiPrefixes: ['/settings/dkv'],
    routePrefixes: [],
    settingsPaths: ['/settings/integrations/dkv'],
    integrationId: 'dkv',
  },
  {
    key: 'meinshk',
    label: 'MeinSHK',
    description: 'Kunden, Projekte und Termine an die MeinSHK-App übergeben, Zeiten und Rapporte zurückholen.',
    group: 'integration',
    defaultEnabled: true,
    // Kunden und Projekte sind der Kern der Übergabe; Plantafel, Aufgaben und Zeiten sind in den
    // MeinSHK-Einstellungen einzeln zuschaltbar und brauchen kein eigenes Modul.
    requires: ['kontakte', 'projekte'],
    apiPrefixes: ['/settings/meinshk'],
    routePrefixes: [],
    settingsPaths: ['/settings/integrations/meinshk'],
    integrationId: 'meinshk',
  },
  {
    key: 'omd',
    label: 'Open Master Data',
    description: 'Live-Produktdaten, Preise und Bilder im Stammdaten-Katalog.',
    group: 'integration',
    defaultEnabled: true,
    requires: ['stammdaten'],
    apiPrefixes: ['/omd', '/settings/omd', '/integrations/omd'],
    routePrefixes: [],
    settingsPaths: ['/settings/integrations/open-master-data', '/settings/omd', '/settings/omd-gc'],
    integrationId: 'omd',
  },
  {
    key: 'ids',
    label: 'IDS',
    description: 'Warenkorb direkt aus dem Angebot an den Großhändler übertragen.',
    group: 'integration',
    defaultEnabled: true,
    requires: ['projekte', 'angebote'],
    apiPrefixes: ['/settings/ids', '/integrations/ids'],
    routePrefixes: [],
    settingsPaths: ['/settings/integrations/ids', '/settings/ids'],
    integrationId: 'ids',
  },
  {
    key: 'metal',
    label: 'Metallnotierungen',
    description: 'Kupfer- und Aluminiumzuschläge mit aktuellen Notierungen berechnen.',
    group: 'integration',
    defaultEnabled: true,
    requires: ['stammdaten', 'angebote'],
    apiPrefixes: ['/metal-quotes'],
    routePrefixes: [],
    settingsPaths: ['/settings/integrations/metallnotierungen'],
    integrationId: 'metal',
  },
  {
    key: 'lead_api',
    label: 'Externe Lead-API',
    description: 'Anfragen aus Website-Formularen und Portalen als Projekt anlegen.',
    group: 'integration',
    defaultEnabled: true,
    requires: ['projekte'],
    apiPrefixes: ['/me/api-keys'],
    routePrefixes: [],
    settingsPaths: ['/settings/users/api'],
    integrationId: 'lead_api',
  },
];

/** Rechte, die kein Modul abschaltet (Einstellungen bleiben immer erreichbar). */
export const ALWAYS_ON_CAPABILITIES: readonly string[] = [
  'web.einstellungen.view',
  'web.einstellungen.edit',
  'web.einstellungen.invite',
];

/** API-Pfade, die mehrere Module teilen: offen, solange mindestens eines an ist. */
export const SHARED_MODULE_API_RULES: readonly { prefix: string; anyOf: readonly InstanceModuleKey[] }[] = [
  { prefix: '/activities', anyOf: ['projekte', 'plantafel'] },
  { prefix: '/settings/lead-categories', anyOf: ['projekte', 'plantafel'] },
  { prefix: '/settings/activity-types', anyOf: ['plantafel', 'projekte', 'posteingang'] },
  { prefix: '/settings/checklists', anyOf: ['plantafel', 'projekte'] },
  { prefix: '/employees/picker', anyOf: ['hr', 'fuhrpark', 'projekte'] },
  { prefix: '/ai/memory-facts', anyOf: ['kichat', 'field_app'] },
];

/**
 * Einzelne Routen, die nicht zum Modul ihres Prefixes passen. Werden vor allen Prefix-Regeln
 * geprüft (erste passende gewinnt). `allOf`: alle Module müssen an sein (Daten eines Moduls in der
 * Oberfläche eines anderen). `anyOf`: eines reicht.
 */
export type ModuleApiRouteRule = {
  pattern: RegExp;
  allOf?: readonly InstanceModuleKey[];
  anyOf?: readonly InstanceModuleKey[];
};

/** Module, aus denen heraus E-Mails mit einem verbundenen Postfach verschickt werden. */
const MAIL_SENDING_MODULES: readonly InstanceModuleKey[] = ['posteingang', 'projekte', 'rechnungen', 'kontakte', 'vorlagen'];

export const MODULE_API_ROUTE_RULES: readonly ModuleApiRouteRule[] = [
  // E-Mail-Versand (Angebote, Rechnungen, Mahnungen) und Postfach-Verbindung gehören nicht zum
  // gemeinsamen Postfach: Posteingang aus blendet nur das Postfach aus.
  { pattern: /^\/inbox\/accounts$/, anyOf: MAIL_SENDING_MODULES },
  { pattern: /^\/inbox\/accounts\/[^/]+$/, anyOf: MAIL_SENDING_MODULES },
  { pattern: /^\/inbox\/accounts\/[^/]+\/(send|signature|visibility|default-from)$/, anyOf: MAIL_SENDING_MODULES },
  { pattern: /^\/inbox\/(dispatch-account|signature-images|imap|oauth)(\/|$)/, anyOf: MAIL_SENDING_MODULES },
  // Rechnungseingang per E-Mail gehört zu Rechnungen.
  { pattern: /^\/inbox\/(incoming-invoice-accounts|accounts\/[^/]+\/incoming-invoice)$/, allOf: ['rechnungen'] },
  // Rechnungen im Projekt
  // (`…/invoice-document-sent` erfasst auch gesendete Angebote; dort prüft der Handler den Belegtyp.)
  { pattern: /^\/leads\/[^/]+\/(incoming-invoices|zugferd-pdf)$/, allOf: ['projekte', 'rechnungen'] },
  { pattern: /^\/offerings\/[^/]+\/(sage-import-documents|position-billing-ledger)$/, allOf: ['projekte', 'rechnungen'] },
  // E-Mail-Verlauf, Zeiten, IDS-Warenkorb und Lieferantenangebote im Projekt
  { pattern: /^\/leads\/[^/]+\/emails$/, allOf: ['projekte', 'posteingang'] },
  { pattern: /^\/leads\/[^/]+\/time-entries(\/|$)/, allOf: ['projekte', 'hr'] },
  { pattern: /^\/leads\/[^/]+\/offerings\/[^/]+\/ids(\/|$)/, allOf: ['angebote', 'ids'] },
  { pattern: /^\/offerings\/[^/]+\/(quote-offers|apply-quote-offer)$/, allOf: ['angebote', 'ausschreibungen'] },
  { pattern: /^\/offerings\/[^/]+\/positions\/recalculate-metal$/, allOf: ['angebote', 'metal'] },
  // Angebotsbelege eines Vorgangs (Prüfung doppelter Angebotsnummern im Editor)
  { pattern: /^\/offerings\/[^/]+\/offer-documents$/, allOf: ['angebote'] },
  // Projekte, Rechnungen und Materialien auf Kontakt- und Organisationsseiten
  { pattern: /^\/(contacts|organizations)\/[^/]+\/leads$/, allOf: ['kontakte', 'projekte'] },
  { pattern: /^\/(contacts|organizations)\/[^/]+\/invoice-documents$/, allOf: ['kontakte', 'rechnungen'] },
  { pattern: /^\/organizations\/[^/]+\/materials$/, allOf: ['kontakte', 'stammdaten'] },
  // Mobile App: Funktionen anderer Module
  { pattern: /^\/field\/(leads|lead-categories)(\/|$)/, allOf: ['field_app', 'projekte'] },
  { pattern: /^\/field\/jobs\/[^/]+\/offerings(\/|$)/, allOf: ['field_app', 'angebote'] },
  { pattern: /^\/field\/jobs\/[^/]+\/work-report$/, allOf: ['field_app', 'auftraege'] },
  { pattern: /^\/field\/jobs\/[^/]+\/(notes|mention-targets|documents)(\/|$)/, allOf: ['field_app', 'projekte'] },
  { pattern: /^\/field\/(customers|organizations|contacts)(\/|$)/, allOf: ['field_app', 'kontakte'] },
  { pattern: /^\/field\/stammdaten(\/|$)/, allOf: ['field_app', 'stammdaten'] },
  { pattern: /^\/field\/jobs\/[^/]+\/invoice$/, allOf: ['field_app', 'rechnungen'] },
  { pattern: /^\/field\/(time-entries|time-entry-change-requests|team-time|clock)(\/|$)/, allOf: ['field_app', 'hr'] },
];

/** Status-Pfade, die auch bei abgewähltem Modul antworten (die UI fragt sie überall). */
export const MODULE_EXEMPT_API_PATHS: readonly string[] = ['/datev/status', '/datev/cost-centers'];

/**
 * Prefixe, die bewusst keinem Modul gehören. Nur Doku und Registertest; die Middleware lässt
 * ohnehin alles durch, was kein Modul trifft.
 */
export const MODULE_OPEN_API_PREFIXES: readonly string[] = [
  '/auth',
  '/me',
  '/settings',
  '/admin',
  '/admin-datanorm',
  '/notifications',
  '/dashboard',
  '/search',
  '/stats',
  '/address',
  '/address-suggest',
  '/routing',
  '/recycle-bin',
  '/compliance',
  '/holidays',
  '/public',
  '/internal',
  '/integrations/v1',
];

const MODULE_BY_KEY: ReadonlyMap<InstanceModuleKey, InstanceModule> = new Map(
  INSTANCE_MODULES.map((module) => [module.key, module]),
);

const modulesByCapability = new Map<string, readonly InstanceModuleKey[]>();

function moduleClaimsCapability(module: InstanceModule, capability: string): boolean {
  if (module.capabilities?.includes(capability)) return true;
  return (module.capabilityPrefixes ?? []).some((prefix) => capability.startsWith(prefix));
}

/**
 * PostgREST/Postgres-Fehlercodes für „Tabelle bzw. Beziehung instance_modules fehlt“ (Migration
 * noch nicht eingespielt). Nur dann gelten die Defaults; andere Fehler werden weitergereicht.
 */
const MISSING_TABLE_CODES = new Set(['PGRST200', 'PGRST205', '42P01']);

export function isMissingModulesTableError(error: { code?: string | null; message?: string | null } | null | undefined): boolean {
  if (!error) return false;
  return MISSING_TABLE_CODES.has(String(error.code ?? '')) && /instance_modules/.test(String(error.message ?? ''));
}

export function isInstanceModuleKey(value: unknown): value is InstanceModuleKey {
  return typeof value === 'string' && (INSTANCE_MODULE_KEYS as readonly string[]).includes(value);
}

export function getInstanceModule(key: InstanceModuleKey): InstanceModule {
  return MODULE_BY_KEY.get(key)!;
}

export function defaultModuleMap(): InstanceModuleMap {
  const map = {} as InstanceModuleMap;
  for (const module of INSTANCE_MODULES) map[module.key] = module.defaultEnabled;
  return map;
}

/**
 * Abhängigkeiten anwenden: ein Modul gilt als aus, wenn ein benötigtes Modul (auch über mehrere
 * Stufen) aus ist. Liefert eine neue Map.
 */
export function applyModuleDependencies(map: InstanceModuleMap): InstanceModuleMap {
  const next = { ...map };
  let changed = true;
  while (changed) {
    changed = false;
    for (const module of INSTANCE_MODULES) {
      if (!next[module.key]) continue;
      if ((module.requires ?? []).some((dep) => !next[dep])) {
        next[module.key] = false;
        changed = true;
      }
    }
  }
  return next;
}

/** Zeilen aus instance_modules über die Defaults legen; unbekannte Keys werden ignoriert. */
export function moduleMapFromRows(
  rows: ReadonlyArray<{ module_key?: unknown; enabled?: unknown }> | null | undefined,
): InstanceModuleMap {
  const map = defaultModuleMap();
  for (const row of rows ?? []) {
    if (!isInstanceModuleKey(row.module_key) || typeof row.enabled !== 'boolean') continue;
    map[row.module_key] = row.enabled;
  }
  return applyModuleDependencies(map);
}

/** Objekt (z. B. aus /api/me) in eine vollständige Map wandeln; Unbekanntes wird ignoriert. */
export function normalizeModuleMap(input: unknown): InstanceModuleMap {
  const map = defaultModuleMap();
  if (!input || typeof input !== 'object' || Array.isArray(input)) return map;
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (isInstanceModuleKey(key) && typeof value === 'boolean') map[key] = value;
  }
  return applyModuleDependencies(map);
}

export function isModuleEnabled(modules: Partial<InstanceModuleMap> | null | undefined, key: InstanceModuleKey): boolean {
  if (!modules) return getInstanceModule(key).defaultEnabled;
  const value = modules[key];
  return typeof value === 'boolean' ? value : getInstanceModule(key).defaultEnabled;
}

export function modulesForCapability(capability: string): readonly InstanceModuleKey[] {
  const cached = modulesByCapability.get(capability);
  if (cached) return cached;
  const owners = INSTANCE_MODULES.filter((module) => moduleClaimsCapability(module, capability)).map((module) => module.key);
  modulesByCapability.set(capability, owners);
  return owners;
}

/** Ein Recht ist verfügbar, wenn alle Module an sind, die es beanspruchen. */
export function isCapabilityAvailable(capability: string, modules: Partial<InstanceModuleMap> | null | undefined): boolean {
  return modulesForCapability(capability).every((key) => isModuleEnabled(modules, key));
}

/** Alle Rechte abgewählter Module auf false setzen (letzter Schritt der Rechteauflösung). */
export function stripDisabledModuleCapabilities<T extends Record<string, boolean>>(
  capabilities: T,
  modules: Partial<InstanceModuleMap> | null | undefined,
): T {
  const next = { ...capabilities };
  for (const key of Object.keys(next)) {
    if (next[key] === true && !isCapabilityAvailable(key, modules)) {
      (next as Record<string, boolean>)[key] = false;
    }
  }
  return next;
}

const matchesPrefix = (path: string, prefix: string): boolean =>
  path === prefix || path.startsWith(`${prefix}/`);

export type ApiPathModuleDecision =
  | { kind: 'open' }
  | { kind: 'anyOf'; modules: readonly InstanceModuleKey[] }
  | { kind: 'allOf'; modules: readonly InstanceModuleKey[] }
  | { kind: 'module'; module: InstanceModuleKey };

/**
 * Welches Modul ein API-Pfad (relativ zu /api) braucht. Express vergleicht Routen ohne Rücksicht
 * auf Groß-/Kleinschreibung, deshalb hier ebenso: sonst käme `/INVOICES/1` an der Sperre vorbei.
 * Reihenfolge: Status-Ausnahmen, einzelne Routen, geteilte Prefixe, längster Modul-Prefix.
 */
export function resolveApiPathModules(rawPath: string): ApiPathModuleDecision {
  const path = rawPath.toLowerCase();
  if (MODULE_EXEMPT_API_PATHS.includes(path)) return { kind: 'open' };
  const route = MODULE_API_ROUTE_RULES.find((rule) => rule.pattern.test(path));
  if (route?.allOf) return route.allOf.length === 1 ? { kind: 'module', module: route.allOf[0] } : { kind: 'allOf', modules: route.allOf };
  if (route?.anyOf) return { kind: 'anyOf', modules: route.anyOf };
  const shared = SHARED_MODULE_API_RULES.find((rule) => matchesPrefix(path, rule.prefix));
  if (shared) return { kind: 'anyOf', modules: shared.anyOf };
  let best: { module: InstanceModuleKey; length: number } | null = null;
  for (const module of INSTANCE_MODULES) {
    for (const prefix of module.apiPrefixes) {
      if (!matchesPrefix(path, prefix)) continue;
      if (!best || prefix.length > best.length) best = { module: module.key, length: prefix.length };
    }
  }
  return best ? { kind: 'module', module: best.module } : { kind: 'open' };
}

/** Modul eines Client-Pfads nach /i/:instanceId (z. B. "/invoices/outgoing"); null = immer an. */
export function moduleForAppPath(pathAfterInstance: string): InstanceModuleKey | null {
  const path = pathAfterInstance.split('?')[0].split('#')[0].toLowerCase();
  let best: { module: InstanceModuleKey; length: number } | null = null;
  for (const module of INSTANCE_MODULES) {
    const prefixes = [...module.routePrefixes, ...(module.settingsPaths ?? [])];
    for (const prefix of prefixes) {
      if (!matchesPrefix(path, prefix)) continue;
      if (!best || prefix.length > best.length) best = { module: module.key, length: prefix.length };
    }
  }
  return best?.module ?? null;
}

/** "/i/<id>/invoices" → "/invoices"; Pfade ohne Instanz-Prefix bleiben wie sie sind. */
export function stripInstancePrefix(path: string): string {
  return path.replace(/^\/i\/[^/]+/i, '') || '/';
}

/** Ist der API-Pfad (relativ zu /api) bei diesen Modulen erlaubt? Für Middleware und Tests. */
export function isApiPathAllowed(path: string, modules: Partial<InstanceModuleMap> | null | undefined): {
  allowed: boolean;
  module?: InstanceModuleKey;
} {
  const decision = resolveApiPathModules(path);
  if (decision.kind === 'open') return { allowed: true };
  if (decision.kind === 'module') {
    return isModuleEnabled(modules, decision.module) ? { allowed: true } : { allowed: false, module: decision.module };
  }
  if (decision.kind === 'anyOf') {
    return decision.modules.some((key) => isModuleEnabled(modules, key))
      ? { allowed: true }
      : { allowed: false, module: decision.modules[0] };
  }
  const missing = decision.modules.find((key) => !isModuleEnabled(modules, key));
  return missing ? { allowed: false, module: missing } : { allowed: true };
}

/** Settings-Seiten, die mehreren Modulen dienen: sichtbar, solange eines davon an ist. */
export const SHARED_MODULE_SETTINGS_PATHS: readonly { path: string; anyOf: readonly InstanceModuleKey[] }[] = [
  { path: '/settings/activity-types', anyOf: ['plantafel', 'projekte', 'posteingang'] },
  { path: '/settings/checklists', anyOf: ['plantafel', 'projekte'] },
  // Postfach und Versand: nur, wenn mindestens ein Modul E-Mails verschickt.
  { path: '/settings/email-dispatch', anyOf: MAIL_SENDING_MODULES },
];

export function isSettingsPathAvailable(path: string, modules: Partial<InstanceModuleMap> | null | undefined): boolean {
  const lower = path.split('?')[0].toLowerCase();
  const shared = SHARED_MODULE_SETTINGS_PATHS.find((entry) => matchesPrefix(lower, entry.path));
  if (shared) return shared.anyOf.some((key) => isModuleEnabled(modules, key));
  const module = moduleForAppPath(path);
  return module === null || isModuleEnabled(modules, module);
}

/** Gehört die Settings-Seite einem Modul (allein oder geteilt)? Dann erst nach dem Laden zeigen. */
export function isModuleSettingsPath(path: string): boolean {
  const lower = path.split('?')[0].toLowerCase();
  return SHARED_MODULE_SETTINGS_PATHS.some((entry) => matchesPrefix(lower, entry.path)) || moduleForAppPath(path) !== null;
}

/**
 * Darf der Nutzer die Seite öffnen? Modul an und, falls das Modul ein Recht verlangt, Recht
 * vorhanden. Für Quer-Links: ist das false, wird Text statt Link gezeigt.
 */
export function canOpenAppPath(
  path: string,
  modules: Partial<InstanceModuleMap> | null | undefined,
  capabilities: Record<string, boolean> | null | undefined,
): boolean {
  const key = moduleForAppPath(stripInstancePrefix(path));
  if (key === null) return true;
  if (!isModuleEnabled(modules, key)) return false;
  const viewCapability = getInstanceModule(key).viewCapability;
  if (!viewCapability) return true;
  return capabilities?.[viewCapability] === true;
}

/** Label für Hinweise („Der Bereich „Rechnungen“ ist nicht freigeschaltet“). */
export function moduleLabelForCapability(capability: string): string | null {
  const keys = modulesForCapability(capability);
  if (keys.length === 0) return null;
  return getInstanceModule(keys[0]).label;
}
